import { useState } from 'react';

import type { ArPdc, ChartOfAccount, ClearArPdc } from '@dcc-erp/shared';
import { dateToInputValue, fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { SearchSelect } from '../SearchSelect';
import {
  modalBody,
  modalBox,
  modalError,
  modalFooter,
  modalHeader,
  modalLabel,
  modalOverlay,
  modalTitle,
} from './modal-style';

// Router-free AR post-dated-cheque (เช็คลงวันที่ล่วงหน้า / PDC) register list. The
// route owns data fetching (useArPdcs) + the lifecycle mutations + the create
// modal; this component owns presentation only — a flat table carrying the
// cheque header (chequeNo / bank / chequeDate / amount / customer) + the
// lifecycle `status` badge + a due-soon/overdue cue on the cheque date. Row
// actions: ดู / นำฝาก (deposit, RECEIVED only) / เช็คผ่าน (clear, RECEIVED|DEPOSITED)
// / เช็คเด้ง (bounce, RECEIVED|DEPOSITED). A PDC posts NO journal entry until it
// CLEARS (and that GL is the receipt the clear creates), so there is no
// engine/approval column. Plain prototype.css classes only.

// Whole days from `now` to a cheque date (chequeDate − today). Negative = the
// cheque date is already in the past. Computed off the date-only string so DST /
// time-of-day don't skew the bucket.
export function dueInDays(chequeDate: string | Date, nowMs: number): number {
  const due = new Date(String(chequeDate).slice(0, 10)).getTime();
  // Derive "today" from LOCAL getters (dateToInputValue), not toISOString (UTC):
  // the cheque date is a local date-only string, so a UTC "today" is off-by-one in
  // positive-UTC zones (Asia/Bangkok) between local midnight and 07:00.
  const today = new Date(dateToInputValue(new Date(nowMs))).getTime();
  return Math.round((due - today) / 864e5);
}

// Due-soon / overdue cue for the cheque-date cell. Only RECEIVED|DEPOSITED
// cheques are "live" (a CLEARED/BOUNCED cheque is terminal, no cue). Red when the
// cheque date is in the past (should already be deposited/cleared), amber when it
// falls due within 7 days.
function ChequeDateCell({ pdc, nowMs }: { pdc: ArPdc; nowMs: number }) {
  const live = pdc.status === 'RECEIVED' || pdc.status === 'DEPOSITED';
  const days = dueInDays(pdc.chequeDate, nowMs);
  let color: string | undefined;
  let title: string | undefined;
  if (live && days < 0) {
    color = 'var(--negative)';
    title = `เลยกำหนดเช็ค ${-days} วัน`;
  } else if (live && days <= 7) {
    color = 'var(--warning)';
    title = `ครบกำหนดเช็คใน ${days} วัน`;
  }
  return (
    <span style={color ? { color, fontWeight: 600 } : undefined} title={title}>
      {fmtDate(pdc.chequeDate)}
      {live && days < 0 && (
        <span className="badge negative" style={{ marginLeft: 6 }}>
          <span className="bdot" />
          เลยกำหนด
        </span>
      )}
      {live && days >= 0 && days <= 7 && (
        <span className="badge warning" style={{ marginLeft: 6 }}>
          <span className="bdot" />
          ใกล้ครบกำหนด
        </span>
      )}
    </span>
  );
}

// Lifecycle-status badge — same vocabulary as the other AR status cells.
function StatusBadge({ status }: { status: ArPdc['status'] }) {
  switch (status) {
    case 'DEPOSITED':
      return (
        <span className="badge info">
          <span className="bdot" />
          นำฝากแล้ว
        </span>
      );
    case 'CLEARED':
      return (
        <span className="badge positive">
          <span className="bdot" />
          เช็คผ่าน
        </span>
      );
    case 'BOUNCED':
      return (
        <span className="badge negative">
          <span className="bdot" />
          เช็คเด้ง
        </span>
      );
    default:
      return (
        <span className="badge">
          <span className="bdot" />
          รับเช็คแล้ว
        </span>
      );
  }
}

export interface PdcListProps {
  pdcs: ArPdc[];
  /** Snapshot of "now" (ms) for the due cue — supplied by the route. */
  nowMs: number;
  onView: (pdc: ArPdc) => void;
  /** Deposit a RECEIVED cheque (RECEIVED → DEPOSITED). Shown only for RECEIVED. */
  onDeposit: (pdc: ArPdc) => void;
  /** Clear a RECEIVED|DEPOSITED cheque — the route opens the ClearPdcModal. */
  onClear: (pdc: ArPdc) => void;
  /** Bounce a RECEIVED|DEPOSITED cheque — the route prompts for a reason. */
  onBounce: (pdc: ArPdc) => void;
}

export function PdcList({ pdcs, nowMs, onView, onDeposit, onClear, onBounce }: PdcListProps) {
  return (
    <div className="tbl-shell">
      <div className="tbl-wrap" style={{ maxHeight: 600 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่เช็ค</th>
              <th>ธนาคาร</th>
              <th>วันที่บนเช็ค</th>
              <th className="num">จำนวนเงิน</th>
              <th>ลูกค้า</th>
              <th>สถานะ</th>
              <th style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {pdcs.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีข้อมูล
                </td>
              </tr>
            ) : (
              pdcs.map((pdc) => {
                const live = pdc.status === 'RECEIVED' || pdc.status === 'DEPOSITED';
                return (
                  <tr key={pdc.id}>
                    <td>
                      <span className="code">{pdc.chequeNo}</span>
                    </td>
                    <td>
                      {pdc.bankName}
                      {pdc.bankBranch ? ` · ${pdc.bankBranch}` : ''}
                    </td>
                    <td>
                      <ChequeDateCell pdc={pdc} nowMs={nowMs} />
                    </td>
                    <td className="num tnum">{fmt(toNumber(pdc.amount), 2)}</td>
                    <td>{pdc.customerName ?? pdc.customerId}</td>
                    <td>
                      <StatusBadge status={pdc.status} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          className="btn ghost"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          onClick={() => onView(pdc)}
                          title="ดูรายละเอียด"
                        >
                          <Icon name="eye" size={12} /> ดู
                        </button>
                        {pdc.status === 'RECEIVED' && (
                          <button
                            className="btn ghost"
                            style={{ padding: '2px 8px', fontSize: 12 }}
                            onClick={() => onDeposit(pdc)}
                            title="นำเช็คเข้าฝากธนาคาร (รับเช็คแล้ว → นำฝากแล้ว)"
                          >
                            <Icon name="ar" size={12} /> นำฝาก
                          </button>
                        )}
                        {live && (
                          <button
                            className="btn primary"
                            style={{ padding: '2px 8px', fontSize: 12 }}
                            onClick={() => onClear(pdc)}
                            title="บันทึกเช็คผ่าน (ลงรับชำระ Dr เงินสด/Cr ลูกหนี้)"
                          >
                            <Icon name="check" size={12} /> เช็คผ่าน
                          </button>
                        )}
                        {live && (
                          <button
                            className="btn ghost"
                            style={{ padding: '2px 8px', fontSize: 12 }}
                            onClick={() => onBounce(pdc)}
                            title="บันทึกเช็คเด้ง (ลูกหนี้ยังคงค้าง)"
                          >
                            <Icon name="x" size={12} /> เช็คเด้ง
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Clear PDC modal — pick the company bank/cash COA the cheque cleared into + the
// cleared date (default today). The clear endpoint creates a receipt over the
// PDC's allocations (Dr Cash / Cr AR), so this is exactly the cash-account picker
// the receipt screen uses. A WHT cheque must NOT clear here (the clear posts a
// plain Dr Cash / Cr AR with no withholding) — note that in the UI.
// =============================================================================

type CashAccount = Pick<ChartOfAccount, 'code' | 'nameTh'>;

export interface ClearPdcModalProps {
  pdc: ArPdc;
  cashAccounts: CashAccount[];
  onSubmit: (data: ClearArPdc) => void;
  onClose: () => void;
  submitting: boolean;
  /**
   * Enriched allocations of the cheque (fetched via the detail query by the
   * route). The clear endpoint rejects a no-allocation cheque, so when this is
   * an empty array (and not still loading) the confirm is disabled + a message
   * shown. `undefined` = still loading the detail (don't gate yet).
   */
  allocations?: ArPdc['allocations'];
  /** True while the detail (allocations) query is in flight. */
  loadingAllocations?: boolean;
  /** Server error from the clear mutation — kept the modal open + shown inline. */
  error?: { message: string } | null;
}

export function ClearPdcModal({
  pdc,
  cashAccounts,
  onSubmit,
  onClose,
  submitting,
  allocations,
  loadingAllocations = false,
  error,
}: ClearPdcModalProps) {
  const today = new Date();
  const [cashAccountCode, setCashAccountCode] = useState<string>('');
  const [clearedDate, setClearedDate] = useState(dateToInputValue(today));

  const cashOptions = cashAccounts.map((a) => ({
    value: a.code,
    label: `${a.code} · ${a.nameTh}`,
  }));
  const cashLabel = (code: string) => {
    const hit = cashAccounts.find((a) => a.code === code);
    return hit ? `${hit.code} · ${hit.nameTh}` : code;
  };

  // The clear posts a receipt over the cheque's allocations; with none, the
  // server rejects "no allocations" — a silent dead-end. Gate the confirm once
  // the detail has loaded and carries an empty allocation array.
  const noAllocations = !loadingAllocations && allocations?.length === 0;

  const disabled = !cashAccountCode || !clearedDate || submitting || noAllocations;

  function handleSubmit() {
    onSubmit({ cashAccountCode, clearedDate });
  }

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalBox, width: 520 }}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>บันทึกเช็คผ่าน</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={modalBody}>
          <div className="muted" style={{ fontSize: 13 }}>
            เช็ค <b>{pdc.chequeNo}</b> · {pdc.bankName} · {fmt(toNumber(pdc.amount), 2)} บาท
          </div>

          <div>
            <label style={modalLabel}>บัญชีเงินสด / ธนาคาร (เช็คเข้าบัญชี)</label>
            <SearchSelect
              value={cashAccountCode}
              onChange={setCashAccountCode}
              options={cashOptions}
              getLabel={cashLabel}
              placeholder="— เลือกบัญชีธนาคาร —"
            />
          </div>

          <div>
            <label style={modalLabel}>วันที่เช็คผ่าน</label>
            <input
              type="date"
              className="input"
              aria-label="cleared-date"
              value={clearedDate}
              onChange={(e) => setClearedDate(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div
            style={{
              fontSize: 13,
              color: 'var(--text-muted)',
              padding: '8px 10px',
              borderRadius: 8,
              background: 'var(--brand-tint)',
            }}
          >
            หากเช็คมีหัก ณ ที่จ่าย ให้บันทึกผ่านหน้ารับชำระแทน — การเช็คผ่านนี้จะลงรับชำระแบบ Dr
            เงินสด / Cr ลูกหนี้ เท่านั้น
          </div>

          {noAllocations && (
            <div style={modalError}>
              เช็คนี้ยังไม่ได้ผูกกับใบแจ้งหนี้ — ผูกก่อนหรือบันทึกผ่านหน้ารับชำระ
            </div>
          )}

          {error && <div style={modalError}>บันทึกไม่สำเร็จ: {error.message}</div>}
        </div>
        <div style={modalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="btn primary" disabled={disabled} onClick={handleSubmit}>
            <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'ยืนยันเช็คผ่าน'}
          </button>
        </div>
      </div>
    </div>
  );
}
